import type {
    ChatStatusItem,
    PageChangeContentItem,
    PageChangeEditLine,
    PageChangeOperation,
    PageChangeSetStatusItem,
    PageLayoutColumn,
    PageLayoutContainer,
} from '../chat-status';
import {blockedLabelKeys, isMenuChange, isPageChangeSetStatusItem} from '../chat-status';
import type {ChangeConfirmations} from './change-confirmations';
import {renderCheckbox, setCheckboxDisabled} from './render-utils';

type Options = {
    labels: Record<string, string>;
    currentPageId: () => string;
    /** Whether the preview is drawn now. It belongs to the Sidebar, so it shows only while the Sidebar is open. */
    isShown: () => boolean;
    /** Whether the page's buttons must wait, because a reply or another button press is still running. */
    isBlocked: () => boolean;
    /** Takes a change out of the set, the same way Skip in the chat does. */
    skip: (set: PageChangeSetStatusItem, operationId: string) => void;
    /** The ticks on changes touching participant input, shared with the chat card. */
    confirmations: ChangeConfirmations;
    /**
     * Someone else changed the page under the preview, such as a widget saved or answered, so the staged changes are
     * checked again on the server and one that no longer holds is marked right away (D4.7).
     */
    onPageChanged: () => void;
};

/** A change shown in the preview, with the set it belongs to and that set's place among the previewed sets. */
type PreviewedChange = {
    set: PageChangeSetStatusItem;
    operation: PageChangeOperation;
    order: number;
};

/** Marks every element the preview adds, so it can find and remove them again. */
const PREVIEW_ATTRIBUTE = 'data-ai-preview';
const EDITED_CLASS = 'howspace-ai-preview-edited';
const REMOVED_CLASS = 'howspace-ai-preview-removed';
const BLOCKED_CLASS = 'is-ai-preview-blocked';
const SHOWING_ORIGINAL_CLASS = 'is-showing-original';
const ORIGINAL_CLASS = 'howspace-ai-preview-original';
const HIGHLIGHT_CLASS = 'is-ai-preview-highlighted';
const MOVED_CLASS = 'howspace-ai-preview-moved';
const MOVE_BLOCKED_CLASS = 'is-ai-preview-move-blocked';
/** A real container, row or column the set empties, which applying it removes. */
const GONE_CLASS = 'is-ai-preview-gone';
/** On a real widget the preview moved: the change that moved it. */
const MOVED_ATTRIBUTE = 'data-ai-preview-moved';
/** On the marker left where a moved widget was: the widget's id. */
const ORIGIN_ATTRIBUTE = 'data-ai-preview-origin';
/** On a real container the preview moved, with everything in it: the change that moved it. */
const CONTAINER_MOVED_ATTRIBUTE = 'data-ai-preview-container-moved';
/** On the marker left where a moved container was: the container's id. */
const CONTAINER_ORIGIN_ATTRIBUTE = 'data-ai-preview-container-origin';
const MOVED_CONTAINER_CLASS = 'howspace-ai-preview-moved-container';
const DESCRIPTION_PREVIEW_LENGTH = 220;
/** How long the page has to be still before a change by someone else has the staged changes checked again. */
const RECHECK_DELAY_MS = 1500;
/** A page that never settles, such as one with a running timer, is checked again at least this often. */
const RECHECK_MAX_WAIT_MS = 10_000;
const WIDTH_CLASSES = ['narrow-width', 'normal-width', 'full-width'];

declare const howspace: any;
declare const jQuery: any;

/**
 * Shows the page the way it will look once the staged changes are applied. Only the browser of the user who staged
 * them shows it, the page itself is not changed.
 *
 * The preview is put together from the real widgets on the page and placeholders, never by rendering a widget
 * from settings that are not saved. An edit shows its new values in place of the widget's own, with the original
 * a click away. An addition is a placeholder drawn where applying the set will put it. A deleted widget collapses
 * to a strip at its place. A change that no longer holds stays on top, marked with the reason, with the widget as it
 * is now a click away (D4.7).
 *
 * The page keeps changing under the preview: live updates replace widgets and containers, and page loads and mode
 * switches replace the whole page. So the preview is laid over the page again whenever the page changes, and every
 * step of that is safe to repeat. A widget someone else updates gets its preview back on top, and one someone
 * deletes takes its preview with it.
 *
 * While a set is being applied, the changes not written yet stay previewed where the user saw them, so the page
 * never looks as if they were dropped. Each one leaves the preview once the real widget has it. A set staged on the
 * page meanwhile is previewed after them.
 *
 * Where things go comes from the page as the server plans it once the set is applied: its containers, rows and
 * columns, and the widgets and additions in each column. The preview walks that layout and puts placeholders and
 * moved widgets where it says, draws the containers and columns a change adds, and gives columns their new widths.
 * Widgets nothing moves are never touched. A moved widget leaves a marker behind, so it goes back when its change is
 * skipped or canceled, and a copy a live update renders in the old place takes over from it. A container moved with
 * everything in it goes to its new place the same way, and back to its marker.
 */
export class PagePreview {
    /** The sets previewed on this page, an applying one first because its additions land first. */
    private sets: PageChangeSetStatusItem[] = [];
    private signature = '';
    private observer: MutationObserver | null = null;
    private frame = 0;
    /** Edits showing the widget's original values instead of the new ones, by preview key. */
    private readonly showingOriginal = new Set<string>();
    /** Additions showing all of their content, by preview key. */
    private readonly expanded = new Set<string>();
    /**
     * A change skipped on the page, and the change next to it. The pressed Skip goes with its change, so focus moves
     * on to the next change once it is gone.
     */
    private refocusAfterSkip: {skipped: string; next: string | null} | null = null;
    /** Every change of the chat's sets by preview key, so a moved widget knows whether its change went in. */
    private readonly known = new Map<string, PageChangeOperation>();
    private recheckTimer = 0;
    /** When the pending recheck was first asked for, 0 when none is pending. */
    private recheckSince = 0;
    /** The widgets on the page by id, read once per sync. */
    private widgets = new Map<string, HTMLElement[]>();
    /** The column the preview last put each moved widget in, by widget id. */
    private readonly placedIn = new Map<string, HTMLElement>();
    /** Moves the page has made itself, by preview key, which the preview leaves to the page from then on. */
    private readonly handedOver = new Set<string>();

    constructor(private readonly options: Options) {
    }

    render(statusItems: ChatStatusItem[]): void {
        statusItems.filter(isPageChangeSetStatusItem).forEach(set => set.operations.forEach(operation => {
            this.known.set(previewKey(set, operation), operation);
        }));
        const pageId = this.options.currentPageId();
        const onPage = this.options.isShown()
            ? statusItems.filter(isPageChangeSetStatusItem).filter(set => set.pageId === pageId)
            : [];
        // Set ids grow over time, so sets being applied stay in the order they were staged.
        this.sets = [
            ...onPage.filter(set => set.status === 'applying').sort((left, right) => left.id.localeCompare(right.id)),
            ...onPage.filter(set => set.status === 'staged'),
        ];

        // Only a staged set's changes are redrawn. An apply only gives its changes outcomes, and sync() takes each
        // one out as it gets it, so the rest of the preview and what the user expanded or focused stays as it is.
        const staged = this.sets.filter(set => set.status === 'staged');
        const signature = JSON.stringify([
            pageId,
            staged.map(set => [set.id, set.revision, set.operations]),
        ]);
        if (signature !== this.signature) {
            this.signature = signature;
            // Moved widgets stay where they are and sync() puts them right, since moving one back and forth again
            // reloads the videos and embeds in it.
            this.withoutObserving(() => this.clear(false));
        }
        this.sync();
    }

    /**
     * Scrolls to a change on the page and moves focus to it. A change not written yet is found in the preview,
     * or for an edit of a widget open in its editor, the widget itself. An applied one is the widget it added or
     * changed. Returns false when the page does not show it, for example in a hidden or collapsed part of it.
     */
    reveal(set: PageChangeSetStatusItem, operation: PageChangeOperation): boolean {
        const page = this.pageElement();
        if (set.pageId !== this.options.currentPageId() || page === null) {
            return false;
        }

        const previewed = isPreviewed(set, operation);
        const widgetId = previewed ? operation.target : operation.widgetId;
        const element = (previewed ? this.previewElementOf(previewKey(set, operation)) : null)
            ?? (widgetId === null ? null : page.querySelector<HTMLElement>(`#widget-${CSS.escape(widgetId)}`));
        if (!(element instanceof HTMLElement) || !page.contains(element) || element.getClientRects().length === 0) {
            return false;
        }

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        element.scrollIntoView({behavior: reducedMotion ? 'auto' : 'smooth', block: 'center'});
        const focusTarget = element.querySelector<HTMLElement>(`[${PREVIEW_ATTRIBUTE}="bar"]`) ?? element;
        if (!focusTarget.hasAttribute('tabindex')) {
            focusTarget.tabIndex = -1;
            focusTarget.addEventListener('blur', () => focusTarget.removeAttribute('tabindex'), {once: true});
        }
        focusTarget.focus({preventScroll: true});
        element.classList.add(HIGHLIGHT_CLASS);
        window.setTimeout(() => element.classList.remove(HIGHLIGHT_CLASS), 1600);

        return true;
    }

    /**
     * Running replies and button presses come and go without the preview changing, so only the buttons follow them.
     */
    syncDisabled(): void {
        const disabled = this.options.isBlocked();
        document.querySelectorAll<HTMLButtonElement | HTMLInputElement>(
            `[${PREVIEW_ATTRIBUTE}] button[data-preview-skip], [${PREVIEW_ATTRIBUTE}] input[data-preview-confirm]`,
        ).forEach(control => {
            if (control instanceof HTMLInputElement) {
                setCheckboxDisabled(control, disabled);
            } else {
                control.disabled = disabled;
            }
        });
        this.refocusAfterSkipped();
    }

    /**
     * A tick given in the chat card shows on the page too, without drawing the page again.
     */
    syncConfirmations(): void {
        document.querySelectorAll<HTMLInputElement>(`[${PREVIEW_ATTRIBUTE}] input[data-preview-confirm]`)
            .forEach(input => {
                const [setId, operationId] = (input.dataset.previewConfirm || '').split(':');
                const set = this.sets.find(candidate => candidate.id === setId);
                if (set && operationId) {
                    input.checked = this.options.confirmations.isTicked(set, operationId);
                }
            });
    }

    /**
     * The Skip pressed on the page goes with its change, which drops focus to the page. Once the request is through
     * and the change is gone, focus moves on to the next change on the page instead.
     */
    private refocusAfterSkipped(): void {
        const refocus = this.refocusAfterSkip;
        if (refocus === null || this.options.isBlocked() || this.skipButtonOf(refocus.skipped) !== null) {
            return;
        }
        this.refocusAfterSkip = null;
        if (refocus.next !== null && (document.activeElement === null || document.activeElement === document.body)) {
            this.skipButtonOf(refocus.next)?.focus({preventScroll: true});
        }
    }

    private skipButtonOf(operationId: string): HTMLButtonElement | null {
        return document.querySelector<HTMLButtonElement>(`[data-preview-skip="${CSS.escape(operationId)}"]`);
    }

    /**
     * The change drawn after this one on the page, or before it when it is the last.
     */
    private neighbourOf(operationId: string): string | null {
        const skips = Array.from(document.querySelectorAll<HTMLButtonElement>('button[data-preview-skip]'));
        const index = skips.findIndex(button => button.dataset.previewSkip === operationId);
        const neighbour = index === -1 ? null : skips[index + 1] ?? skips[index - 1] ?? null;

        return neighbour?.dataset.previewSkip ?? null;
    }

    destroy(): void {
        this.sets = [];
        this.stopObserving();
        window.clearTimeout(this.recheckTimer);
        this.clear();
    }

    private sync(): void {
        this.withoutObserving(() => {
            const page = this.pageElement();
            if (this.sets.length === 0 || page === null) {
                this.clear();
                return;
            }

            const previewed = this.sets.flatMap((set, order) => set.operations
                .filter(operation => isPreviewed(set, operation))
                .map(operation => ({set, operation, order})));
            // The page editor works out where a widget goes from the elements around it, so while the page is
            // edited, nothing of the preview is put between them.
            const editing = document.body.classList.contains('edit-page') || document.body.classList.contains('dragging');
            const placed = editing ? [] : previewed;
            this.widgets = widgetsOf(page);
            this.arrange(page, placed, editing ? null : this.layoutToShow());
            this.syncEdits(page, previewed.filter(change => (
                change.operation.op === 'update_item' || change.operation.op === 'remove_item'
            )));
            this.syncMoves(page, placed.filter(change => isWidgetMove(change.operation)));
            this.syncContainerMoves(page, placed.filter(change => isContainerMove(change.operation)));
            this.syncLayoutChanges(page, placed.filter(change => change.operation.op === 'set_layout'));
            this.syncDisabled();
        });

        if (this.sets.length === 0) {
            this.stopObserving();
        } else {
            this.observe();
        }
    }

    /**
     * Edits and deletes, each drawn over the widget it changes.
     */
    private syncEdits(page: HTMLElement, edits: PreviewedChange[]): void {
        const current = new Set<HTMLElement>();
        // A widget shows one change. The newest one is the one it will end up with.
        [...edits].reverse().forEach(({set, operation: edit}) => {
            if (edit.target === null) {
                return;
            }
            const key = previewKey(set, edit);
            // A widget open for editing shows its own form, never the preview.
            const selector = `.widget[data-widget-id="${CSS.escape(edit.target)}"][data-widget-mode="view"]`;
            page.querySelectorAll<HTMLElement>(selector)
                .forEach(widget => {
                    if (current.has(widget)) {
                        return;
                    }
                    // The copy a live update is about to replace keeps its preview until it is gone.
                    if (widget.dataset.aiPreviewOp !== key) {
                        this.undecorate(widget);
                        if (edit.op === 'remove_item') {
                            this.decorateRemoval(widget, set, edit);
                        } else {
                            this.decorateEdit(widget, set, edit);
                        }
                    }
                    widget.classList.toggle(SHOWING_ORIGINAL_CLASS, this.showingOriginal.has(key));
                    this.syncToggle(widget, key);
                    current.add(widget);
                });
        });

        page.querySelectorAll<HTMLElement>(`.${EDITED_CLASS}, .${REMOVED_CLASS}`).forEach(widget => {
            if (!current.has(widget)) {
                // Only focus on the preview's own controls, which go with it. The widget's own keep theirs.
                const focusedPreview = document.activeElement?.closest(`[${PREVIEW_ATTRIBUTE}]`);
                const hadFocus = focusedPreview instanceof HTMLElement && widget.contains(focusedPreview);
                this.undecorate(widget);
                if (hadFocus) {
                    focusWithoutScrolling(widget);
                }
            }
        });
    }

    private decorateEdit(widget: HTMLElement, set: PageChangeSetStatusItem, edit: PageChangeOperation): void {
        const key = previewKey(set, edit);
        widget.classList.add(EDITED_CLASS);
        widget.classList.toggle(BLOCKED_CLASS, edit.blocked !== null);
        widget.dataset.aiPreviewOp = key;

        const bar = this.renderBar(set, edit, this.label('previewEdited'));
        bar.querySelector('.howspace-ai-preview-actions')?.prepend(this.renderToggle(key));
        widget.prepend(bar);

        let anchor: Element = bar;
        const lines = edit.changes.filter(line => line.kind !== 'title' && line.kind !== 'text');
        if (lines.length > 0) {
            anchor = this.renderEditLines(lines);
            bar.after(anchor);
        }
        const title = edit.fields.title;
        if (title !== undefined) {
            const heading = document.createElement('h2');
            heading.className = 'widget-title widget-padding break-word';
            heading.setAttribute(PREVIEW_ATTRIBUTE, 'value');
            const text = document.createElement('span');
            text.className = 'widget-title-text';
            text.textContent = title;
            heading.append(text);
            anchor = this.replaceInPlace(widget, '.widget-title', heading, anchor);
        }

        const body = edit.fields.text;
        if (body !== undefined) {
            const description = document.createElement('div');
            description.className = 'widget-description widget-padding break-word';
            description.setAttribute(PREVIEW_ATTRIBUTE, 'value');
            // Cleaned on the server by the same sanitizer the widget is saved through.
            description.innerHTML = body;
            this.replaceInPlace(widget, '.widget-description', description, anchor);
        }
    }

    /**
     * A widget the set deletes collapses to its strip, and opens to show what goes.
     */
    private decorateRemoval(widget: HTMLElement, set: PageChangeSetStatusItem, removal: PageChangeOperation): void {
        const key = previewKey(set, removal);
        widget.classList.add(REMOVED_CLASS);
        widget.classList.toggle(BLOCKED_CLASS, removal.blocked !== null);
        widget.dataset.aiPreviewOp = key;

        widget.prepend(this.renderBar(set, removal, this.label('previewRemoved')));
    }

    /**
     * Shows the widget as it is now instead of the change, or for a delete, the widget that goes.
     */
    private renderToggle(key: string): HTMLButtonElement {
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'howspace-ai-preview-toggle';
        toggle.dataset.previewToggle = key;
        toggle.addEventListener('click', () => {
            if (this.showingOriginal.has(key)) {
                this.showingOriginal.delete(key);
            } else {
                this.showingOriginal.add(key);
            }
            this.sync();
        });

        return toggle;
    }

    /**
     * What an edit changes beyond its title and text, which show in place: items added, changed, removed or
     * reordered, and settings.
     */
    private renderEditLines(lines: PageChangeEditLine[]): HTMLElement {
        const list = document.createElement('ul');
        list.className = 'howspace-ai-preview-changes';
        list.setAttribute(PREVIEW_ATTRIBUTE, 'value');
        lines.forEach(line => {
            const entry = document.createElement('li');
            entry.className = `is-${line.kind}${line.option ? ' is-option' : ''}`;
            entry.textContent = this.editLineText(line);
            list.append(entry);
        });

        return list;
    }

    private editLineText(line: PageChangeEditLine): string {
        if (line.kind === 'setting') {
            const name = this.label(`setting_${line.name ?? ''}`) || line.name || '';
            return `${name}: ${this.settingValue(line.text)}`;
        }
        const key = line.kind === 'changed' && line.before !== null ? 'change_changedFrom' : `change_${line.kind}`;

        return this.label(key).replace(
            /%(text|before)%/g,
            (_match, name: string) => (name === 'text' ? line.text : line.before ?? ''),
        );
    }

    private settingValue(value: string): string {
        if (value === 'true' || value === 'false') {
            return this.label(value === 'true' ? 'settingOn' : 'settingOff');
        }

        return this.label(`value_${value}`) || value;
    }

    /**
     * Puts the new value right after the original one and marks the original, so either can be shown. A widget
     * without the original element gets the new one after the previous value, or after the bar.
     */
    private replaceInPlace(widget: HTMLElement, selector: string, value: HTMLElement, fallbackAnchor: Element): Element {
        const original = Array.from(widget.querySelectorAll<HTMLElement>(selector))
            .find(element => !element.hasAttribute(PREVIEW_ATTRIBUTE));
        if (original) {
            original.classList.add(ORIGINAL_CLASS);
            original.after(value);
        } else {
            fallbackAnchor.after(value);
        }

        return value;
    }

    private syncToggle(widget: HTMLElement, key: string): void {
        const toggle = widget.querySelector<HTMLButtonElement>(`[data-preview-toggle="${CSS.escape(key)}"]`);
        if (!toggle) {
            return;
        }
        const showingOriginal = this.showingOriginal.has(key);
        const original = widget.classList.contains(BLOCKED_CLASS) ? 'previewShowCurrent' : 'previewShowOriginal';
        toggle.textContent = this.label(showingOriginal ? 'previewShowChange' : original);
        toggle.setAttribute('aria-pressed', String(showingOriginal));
    }

    private undecorate(widget: HTMLElement): void {
        widget.querySelectorAll(`[${PREVIEW_ATTRIBUTE}]:not([${PREVIEW_ATTRIBUTE}="move-bar"])`)
            .forEach(element => element.remove());
        widget.querySelectorAll(`.${ORIGINAL_CLASS}`).forEach(element => element.classList.remove(ORIGINAL_CLASS));
        widget.classList.remove(EDITED_CLASS, REMOVED_CLASS, BLOCKED_CLASS, SHOWING_ORIGINAL_CLASS, HIGHLIGHT_CLASS);
        delete widget.dataset.aiPreviewOp;
    }

    /**
     * Lays the planned page over the real one: placeholders and moved widgets where the layout puts them, the
     * containers and columns changes add, the widths changes set, and the parts changes empty hidden.
     */
    private arrange(page: HTMLElement, previewed: PreviewedChange[], layout: PageLayoutContainer[] | null): void {
        const additions = new Map(previewed
            .filter(change => change.operation.op === 'add_widget')
            .map(change => [previewKey(change.set, change.operation), change]));
        const moves = new Map(previewed
            .filter(change => isWidgetMove(change.operation) && change.operation.target !== null)
            .map(change => [change.operation.target as string, change]));
        const containerMoves = new Map(previewed
            .filter(change => isContainerMove(change.operation) && change.operation.containerId !== null)
            .map(change => [change.operation.containerId as string, change]));
        page.querySelectorAll<HTMLElement>(`[${CONTAINER_MOVED_ATTRIBUTE}]`).forEach(container => {
            if (layout === null || !containerMoves.has(container.dataset.containerId || '')) {
                this.restoreMovedContainer(container);
            }
        });

        this.dropPlaceholders(page, layout === null ? new Map() : additions);
        page.querySelectorAll<HTMLElement>(`[${MOVED_ATTRIBUTE}]`).forEach(widget => {
            if (layout === null || !moves.has(widget.dataset.widgetId || '')) {
                this.restoreMoved(widget);
            }
        });

        const kept = new Set<HTMLElement>();
        if (layout !== null) {
            const parent = page.querySelector<HTMLElement>('.page-containers') ?? page;
            let previousContainer: HTMLElement | null = null;
            layout.forEach(container => {
                const element = container.id === null
                    ? this.newContainer(parent, container, previousContainer)
                    : page.querySelector<HTMLElement>(`#container-${CSS.escape(container.id)}`);
                if (element === null) {
                    return;
                }
                kept.add(element);
                if (container.id !== null) {
                    previewContainerWidth(element, container.width);
                    const containerMove = containerMoves.get(container.id);
                    if (containerMove !== undefined) {
                        placeContainer(parent, element, previousContainer);
                        const key = previewKey(containerMove.set, containerMove.operation);
                        element.setAttribute(CONTAINER_MOVED_ATTRIBUTE, key);
                    }
                }
                container.rows.forEach(row => {
                    const rowElement = row.id === null
                        ? element.querySelector<HTMLElement>(`[data-ai-preview-row="${CSS.escape(row.key)}"]`)
                        : element.querySelector<HTMLElement>(`.row[data-row-id="${CSS.escape(row.id)}"]`);
                    if (rowElement === null) {
                        return;
                    }
                    kept.add(rowElement);
                    let previousColumn: HTMLElement | null = null;
                    row.columns.forEach(column => {
                        const columnElement = column.id === null
                            ? this.newColumn(rowElement, column, previousColumn)
                            : rowElement.querySelector<HTMLElement>(`.column[data-column-id="${CSS.escape(column.id)}"]`);
                        if (columnElement === null) {
                            return;
                        }
                        kept.add(columnElement);
                        previewColumnWidth(columnElement, column.width);
                        this.fillColumn(columnElement, column, additions, moves);
                        previousColumn = columnElement;
                    });
                });
                previousContainer = element;
            });
        }

        this.syncGone(page, layout === null ? null : kept);
        page.querySelectorAll<HTMLElement>('[data-ai-preview-width]').forEach(column => {
            if (!kept.has(column)) {
                restoreColumnWidth(column);
            }
        });
        page.querySelectorAll<HTMLElement>('[data-ai-preview-container-width]').forEach(container => {
            if (!kept.has(container)) {
                restoreContainerWidth(container);
            }
        });
    }

    /**
     * The layout of the newest set staged on the page, which plans the additions of sets still being applied too.
     * Otherwise the one the user approved for the set being applied.
     */
    private layoutToShow(): PageLayoutContainer[] | null {
        const staged = [...this.sets].reverse().find(set => set.status === 'staged' && set.layout !== null);

        return staged?.layout ?? this.sets.find(set => set.layout !== null)?.layout ?? null;
    }

    /**
     * Puts each placeholder and moved widget of a column right after the item before it in the layout. Widgets
     * nothing moves stay where they are, and serve as the places the others go after.
     */
    private fillColumn(
        columnElement: HTMLElement,
        column: PageLayoutColumn,
        additions: Map<string, PreviewedChange>,
        moves: Map<string, PreviewedChange>,
    ): void {
        let previous: HTMLElement | null = null;
        column.items.forEach(item => {
            const addition = additions.get(item);
            const move = moves.get(item);
            let element: HTMLElement | null;
            if (addition !== undefined) {
                element = this.previewElementOf(item) ?? this.renderPlaceholder(addition.set, addition.operation);
            } else {
                // An addition an apply has written is its widget by now.
                element = this.liveWidget(this.known.get(item)?.widgetId ?? item);
            }
            if (element === null) {
                return;
            }
            if (move !== undefined && this.handOver(element, previewKey(move.set, move.operation))) {
                previous = element;
                return;
            }
            if (addition !== undefined || move !== undefined) {
                insertAfter(columnElement, previous?.parentElement === columnElement ? previous : null, element);
            }
            if (move !== undefined) {
                element.setAttribute(MOVED_ATTRIBUTE, previewKey(move.set, move.operation));
                this.placedIn.set(element.dataset.widgetId || '', columnElement);
            }
            previous = element;
        });
    }

    /**
     * Whether the page has moved a widget the preview had moved, which happens when its move is applied while the
     * preview still shows it, or someone else moves it. The page's own place stands from then on: moving the widget
     * back would take it out of the column the page put it in.
     */
    private handOver(widget: HTMLElement, key: string): boolean {
        if (this.handedOver.has(key)) {
            return true;
        }
        const placedIn = this.placedIn.get(widget.dataset.widgetId || '');
        if (!widget.hasAttribute(MOVED_ATTRIBUTE) || placedIn === undefined || widget.parentElement === placedIn) {
            return false;
        }
        this.handedOver.add(key);
        widget.removeAttribute(MOVED_ATTRIBUTE);
        document.querySelectorAll(`[${ORIGIN_ATTRIBUTE}="${CSS.escape(widget.dataset.widgetId || '')}"]`)
            .forEach(origin => origin.remove());

        return true;
    }

    /**
     * The widget on the page with this id. A copy a live update rendered in the widget's old place, while the preview
     * had moved it, is the current one, so it takes over and the moved copy goes.
     */
    private liveWidget(widgetId: string): HTMLElement | null {
        const copies = (this.widgets.get(widgetId) ?? []).filter(copy => copy.isConnected);
        const fresh = copies.filter(copy => !copy.hasAttribute(MOVED_ATTRIBUTE));
        if (fresh.length > 0 && fresh.length < copies.length) {
            // The marker where the widget was stays, so the fresh copy still goes back there.
            copies.filter(copy => copy.hasAttribute(MOVED_ATTRIBUTE)).forEach(discardWidget);
            this.widgets.set(widgetId, fresh);
        }

        return fresh[0] ?? copies[0] ?? null;
    }

    /**
     * Puts a moved widget back where it was. Once its change is applied, the page has put it in its new place
     * already, and only the preview's marks go.
     */
    private restoreMoved(widget: HTMLElement): void {
        const widgetId = widget.dataset.widgetId || '';
        const key = widget.getAttribute(MOVED_ATTRIBUTE) || '';
        const origin = document.querySelector<HTMLElement>(`[${ORIGIN_ATTRIBUTE}="${CSS.escape(widgetId)}"]`);
        const others = Array.from(document.querySelectorAll(`.widget[data-widget-id="${CSS.escape(widgetId)}"]`))
            .filter(copy => copy !== widget);
        const applied = this.known.get(key)?.status === 'applied';
        if (!applied && others.length > 0) {
            discardWidget(widget);
        } else if (!applied && origin !== null) {
            origin.before(widget);
        }
        origin?.remove();
        widget.removeAttribute(MOVED_ATTRIBUTE);
        this.undecorateMove(widget);
    }

    /**
     * Placeholders whose addition is no longer previewed go. One goes when its widget arrives, so focus moves on to
     * the widget.
     */
    private dropPlaceholders(page: HTMLElement, additions: Map<string, PreviewedChange>): void {
        page.querySelectorAll<HTMLElement>('[data-ai-preview-placeholder]').forEach(placeholder => {
            const key = placeholder.dataset.aiPreviewPlaceholder || '';
            if (additions.has(key)) {
                return;
            }
            const hadFocus = placeholder.contains(document.activeElement);
            placeholder.remove();
            const widgetId = hadFocus ? this.known.get(key)?.widgetId : null;
            const widget = widgetId ? page.querySelector<HTMLElement>(`#widget-${CSS.escape(widgetId)}`) : null;
            if (widget) {
                focusWithoutScrolling(widget);
            }
        });
    }

    /**
     * A container a change adds, drawn right after the container before it in the layout.
     */
    private newContainer(
        parent: HTMLElement,
        container: PageLayoutContainer,
        previous: HTMLElement | null,
    ): HTMLElement {
        let element = parent.querySelector<HTMLElement>(`[data-ai-preview-container="${CSS.escape(container.key)}"]`);
        if (element === null) {
            element = document.createElement('div');
            element.className = 'howspace-ai-preview-container';
            element.setAttribute(PREVIEW_ATTRIBUTE, 'container');
            element.dataset.aiPreviewContainer = container.key;
            container.rows.forEach(row => {
                const rowElement = document.createElement('div');
                rowElement.className = 'howspace-ai-preview-row';
                rowElement.dataset.aiPreviewRow = row.key;
                element?.append(rowElement);
            });
        }
        element.dataset.aiPreviewWidth = container.width;
        if (previous === null) {
            const first = parent.querySelector(':scope > .page-container, :scope > .howspace-ai-preview-container');
            if (first !== element) {
                if (first) {
                    first.before(element);
                } else {
                    parent.append(element);
                }
            }
        } else if (previous.nextElementSibling !== element) {
            previous.after(element);
        }

        return element;
    }

    /**
     * A column a change adds to a row, drawn right after the column before it in the layout.
     *
     * A real row alternates column spaces and columns, and the page's live updates find where a column goes from the
     * space next to it. So a column drawn into a real row comes with a space of its own after it, and the pair goes
     * after the space that follows the column before it, the way the page adds a column.
     */
    private newColumn(row: HTMLElement, column: PageLayoutColumn, previous: HTMLElement | null): HTMLElement {
        let element = row.querySelector<HTMLElement>(`[data-ai-preview-column="${CSS.escape(column.key)}"]`);
        if (element === null) {
            element = document.createElement('div');
            element.className = 'howspace-ai-preview-column';
            element.setAttribute(PREVIEW_ATTRIBUTE, 'column');
            element.dataset.aiPreviewColumn = column.key;
        }
        const real = row.matches('.row[data-row-id]');
        let space = real ? row.querySelector<HTMLElement>(`[data-ai-preview-space="${CSS.escape(column.key)}"]`) : null;
        if (real && space === null) {
            space = document.createElement('div');
            space.className = 'column-space flex';
            space.setAttribute(PREVIEW_ATTRIBUTE, 'column-space');
            space.dataset.aiPreviewSpace = column.key;
        }

        const after = previous === null
            ? null
            : (real && previous.nextElementSibling?.classList.contains('column-space') ? previous.nextElementSibling : previous);
        const first = row.querySelector(':scope > .column, :scope > .howspace-ai-preview-column');
        const inPlace = after === null ? first === element : after.nextElementSibling === element;
        if (!inPlace) {
            if (after !== null) {
                after.after(element);
            } else if (first !== null) {
                first.before(element);
            } else {
                row.append(element);
            }
        }
        if (space !== null && element.nextElementSibling !== space) {
            element.after(space);
        }

        return element;
    }

    /**
     * Hides the real containers, rows and columns the layout leaves out once they hold nothing, because applying the
     * set removes them. One that still holds widgets came after the layout was planned, and stays.
     */
    private syncGone(page: HTMLElement, kept: Set<HTMLElement> | null): void {
        page.querySelectorAll<HTMLElement>('.page-container[data-container-id], .row[data-row-id], .column[data-column-id]')
            .forEach(element => {
                const empty = element.querySelector('.widget, [data-ai-preview-placeholder]') === null;
                element.classList.toggle(GONE_CLASS, kept !== null && !kept.has(element) && empty);
            });
        page.querySelectorAll<HTMLElement>('.howspace-ai-preview-container, .howspace-ai-preview-column').forEach(element => {
            if (kept === null || !kept.has(element)) {
                this.removeDrawn(element);
            }
        });
    }

    /**
     * Removes a container or column the preview drew. A real widget in it goes back where it was first, or when it
     * has nowhere to go back to, such as one an apply moved there, stays on the page next to it.
     */
    private removeDrawn(element: HTMLElement): void {
        element.querySelectorAll<HTMLElement>('.widget[data-widget-id]').forEach(widget => {
            if (widget.hasAttribute(MOVED_ATTRIBUTE)) {
                this.restoreMoved(widget);
            }
            if (element.contains(widget)) {
                element.before(widget);
            }
        });
        const key = element.dataset.aiPreviewColumn;
        if (key !== undefined) {
            document.querySelectorAll(`[data-ai-preview-space="${CSS.escape(key)}"]`).forEach(space => space.remove());
        }
        element.remove();
    }

    /**
     * The strip on a moved widget, at its new place.
     */
    private syncMoves(page: HTMLElement, moves: PreviewedChange[]): void {
        const current = new Set<HTMLElement>();
        moves.forEach(({set, operation: move}) => {
            const widget = move.target === null ? null : this.liveWidget(move.target);
            if (widget === null) {
                return;
            }
            current.add(widget);
            const key = previewKey(set, move);
            const bar = widget.querySelector<HTMLElement>(`:scope > [${PREVIEW_ATTRIBUTE}="move-bar"]`);
            if (bar?.dataset.aiPreviewMoveOp === key && widget.classList.contains(MOVED_CLASS)) {
                return;
            }
            this.undecorateMove(widget);
            widget.classList.add(MOVED_CLASS);
            widget.classList.toggle(MOVE_BLOCKED_CLASS, move.blocked !== null);
            const moveBar = this.renderBar(set, move, this.label('previewMoved'));
            moveBar.setAttribute(PREVIEW_ATTRIBUTE, 'move-bar');
            moveBar.dataset.aiPreviewMoveOp = key;
            const visibility = this.visibilityNote(move);
            if (visibility !== null) {
                moveBar.append(visibility);
            }
            widget.prepend(moveBar);
        });

        page.querySelectorAll<HTMLElement>(`.${MOVED_CLASS}`).forEach(widget => {
            if (!current.has(widget)) {
                this.undecorateMove(widget);
            }
        });
    }

    /**
     * The strip on a container moved with everything in it, at its new place.
     */
    private syncContainerMoves(page: HTMLElement, moves: PreviewedChange[]): void {
        const current = new Set<HTMLElement>();
        moves.forEach(({set, operation: move}) => {
            const container = move.containerId === null
                ? null
                : page.querySelector<HTMLElement>(`#container-${CSS.escape(move.containerId)}`);
            if (container === null) {
                return;
            }
            current.add(container);
            const key = previewKey(set, move);
            const bar = container.querySelector<HTMLElement>(`:scope > [${PREVIEW_ATTRIBUTE}="container-move-bar"]`);
            if (bar?.dataset.aiPreviewMoveOp === key && container.classList.contains(MOVED_CONTAINER_CLASS)) {
                return;
            }
            this.undecorateContainerMove(container);
            container.classList.add(MOVED_CONTAINER_CLASS);
            container.classList.toggle(MOVE_BLOCKED_CLASS, move.blocked !== null);
            const moveBar = this.renderBar(set, move, this.label('previewMoved'));
            moveBar.setAttribute(PREVIEW_ATTRIBUTE, 'container-move-bar');
            moveBar.dataset.aiPreviewMoveOp = key;
            container.prepend(moveBar);
        });

        page.querySelectorAll<HTMLElement>(`.${MOVED_CONTAINER_CLASS}`).forEach(container => {
            if (!current.has(container)) {
                this.undecorateContainerMove(container);
            }
        });
    }

    private undecorateContainerMove(container: HTMLElement): void {
        container.querySelectorAll(`:scope > [${PREVIEW_ATTRIBUTE}="container-move-bar"]`).forEach(bar => bar.remove());
        container.classList.remove(MOVED_CONTAINER_CLASS, MOVE_BLOCKED_CLASS);
    }

    /**
     * Puts a moved container back where it was. Once its change is applied, the page has put it in its new place
     * already, and only the preview's marks go.
     */
    private restoreMovedContainer(container: HTMLElement): void {
        const containerId = container.dataset.containerId || '';
        const key = container.getAttribute(CONTAINER_MOVED_ATTRIBUTE) || '';
        const origin = document.querySelector<HTMLElement>(
            `[${CONTAINER_ORIGIN_ATTRIBUTE}="${CSS.escape(containerId)}"]`,
        );
        if (this.known.get(key)?.status !== 'applied' && origin !== null) {
            origin.before(container);
        }
        origin?.remove();
        container.removeAttribute(CONTAINER_MOVED_ATTRIBUTE);
        this.undecorateContainerMove(container);
    }

    private undecorateMove(widget: HTMLElement): void {
        widget.querySelectorAll(`:scope > [${PREVIEW_ATTRIBUTE}="move-bar"]`).forEach(bar => bar.remove());
        widget.classList.remove(MOVED_CLASS, MOVE_BLOCKED_CLASS);
    }

    /**
     * A layout change shows its new widths in place, with a strip above the row it changes.
     */
    private syncLayoutChanges(page: HTMLElement, changes: PreviewedChange[]): void {
        const wanted = new Set<string>();
        changes.forEach(({set, operation: change}) => {
            const key = previewKey(set, change);
            wanted.add(key);
            const anchor = this.anchorElement(page, set, change.placement?.anchorId ?? change.target ?? '');
            const row = anchor?.closest<HTMLElement>('.row, .howspace-ai-preview-row') ?? null;
            if (row === null) {
                return;
            }
            const existing = page.querySelector<HTMLElement>(`[data-ai-preview-layout="${CSS.escape(key)}"]`);
            if (existing !== null && existing.nextElementSibling === row) {
                return;
            }
            existing?.remove();

            const strip = document.createElement('section');
            strip.className = 'howspace-ai-preview-layout';
            strip.setAttribute(PREVIEW_ATTRIBUTE, 'layout');
            strip.dataset.aiPreviewLayout = key;
            strip.tabIndex = -1;
            strip.append(this.renderBar(set, change, this.label('previewLayout')));
            const description = document.createElement('p');
            description.className = 'howspace-ai-preview-layout-text';
            description.textContent = this.layoutText(change);
            strip.append(description);
            row.before(strip);
        });

        page.querySelectorAll<HTMLElement>('[data-ai-preview-layout]').forEach(strip => {
            if (!wanted.has(strip.dataset.aiPreviewLayout || '')) {
                strip.remove();
            }
        });
    }

    private layoutText(change: PageChangeOperation): string {
        const parts: string[] = [];
        if (change.widths !== null) {
            parts.push(this.label('lineWidths').replace('%widths%', change.widths.map(width => `${width}%`).join(' / ')));
        }
        if (change.containerWidth !== null) {
            parts.push(this.label('lineContainerWidth').replace(
                '%width%',
                this.label(`containerWidth_${change.containerWidth}`) || change.containerWidth,
            ));
        }

        return parts.join('. ');
    }

    /**
     * The element of what a layout change is placed by: a widget, a staged addition of the set, or a column.
     */
    private anchorElement(page: HTMLElement, set: PageChangeSetStatusItem, anchor: string): HTMLElement | null {
        if (/^op\d+$/.test(anchor)) {
            return this.previewElementOf(`${set.id}:${anchor}`);
        }

        return page.querySelector<HTMLElement>(`.widget[data-widget-id="${CSS.escape(anchor)}"]`)
            ?? page.querySelector<HTMLElement>(`.column[data-column-id="${CSS.escape(anchor)}"]`);
    }

    /**
     * Who sees a moved or placed widget in its new place, when that changes.
     */
    private visibilityNote(operation: PageChangeOperation): HTMLElement | null {
        const text = operation.visibility === null ? '' : this.label(`visibility_${operation.visibility}`);
        if (text === '') {
            return null;
        }
        const note = document.createElement('p');
        note.className = 'howspace-ai-preview-note';
        note.textContent = text;

        return note;
    }

    private renderPlaceholder(set: PageChangeSetStatusItem, addition: PageChangeOperation): HTMLElement {
        const placeholder = document.createElement('section');
        placeholder.className = 'howspace-ai-preview-placeholder';
        placeholder.setAttribute(PREVIEW_ATTRIBUTE, 'placeholder');
        placeholder.dataset.aiPreviewPlaceholder = previewKey(set, addition);
        placeholder.tabIndex = -1;
        this.fillPlaceholder(placeholder, set, addition);

        return placeholder;
    }

    private fillPlaceholder(placeholder: HTMLElement, set: PageChangeSetStatusItem, addition: PageChangeOperation): void {
        const titleId = `howspace-ai-preview-title-${set.id}-${addition.id}`;
        placeholder.setAttribute('aria-labelledby', titleId);

        const heading = document.createElement('h3');
        heading.className = 'howspace-ai-preview-title';
        const icon = document.createElement('i');
        icon.className = `fa-light fa-${addition.icon || 'square'}`;
        icon.setAttribute('aria-hidden', 'true');
        const title = document.createElement('span');
        title.id = titleId;
        title.textContent = addition.title || this.label('untitled');
        heading.append(icon, title);

        const bar = this.renderBar(set, addition, this.label('previewNew'));
        const visibility = this.visibilityNote(addition);
        if (visibility !== null) {
            bar.append(visibility);
        }
        const children: HTMLElement[] = [bar, heading];
        if (addition.source) {
            const source = document.createElement('p');
            source.className = 'howspace-ai-preview-source';
            source.textContent = this.label('previewSource').replace('%title%', addition.source);
            children.push(source);
        }

        const key = previewKey(set, addition);
        const expanded = this.expanded.has(key);
        const hasMore = addition.items.length > 0 || addition.description.length > DESCRIPTION_PREVIEW_LENGTH;
        if (addition.description) {
            const description = document.createElement('p');
            description.className = 'howspace-ai-preview-description';
            description.textContent = expanded || addition.description.length <= DESCRIPTION_PREVIEW_LENGTH
                ? addition.description
                : `${addition.description.slice(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd()}…`;
            children.push(description);
        }

        const contentId = `howspace-ai-preview-content-${set.id}-${addition.id}`;
        if (expanded && addition.items.length > 0) {
            const items = this.renderItems(addition.items);
            items.id = contentId;
            children.push(items);
        }

        if (hasMore) {
            const toggle = document.createElement('button');
            toggle.type = 'button';
            toggle.className = 'howspace-ai-preview-expand';
            toggle.textContent = this.label(expanded ? 'previewHideContent' : 'previewShowContent');
            toggle.setAttribute('aria-expanded', String(expanded));
            if (expanded && addition.items.length > 0) {
                toggle.setAttribute('aria-controls', contentId);
            }
            toggle.addEventListener('click', () => {
                if (this.expanded.has(key)) {
                    this.expanded.delete(key);
                } else {
                    this.expanded.add(key);
                }
                this.withoutObserving(() => {
                    placeholder.replaceChildren();
                    this.fillPlaceholder(placeholder, set, addition);
                    this.syncDisabled();
                });
                placeholder.querySelector<HTMLElement>('.howspace-ai-preview-expand')?.focus();
            });
            children.push(toggle);
        }

        placeholder.replaceChildren(...children);
    }

    private renderItems(items: PageChangeContentItem[]): HTMLElement {
        const list = document.createElement('ul');
        list.className = 'howspace-ai-preview-items';
        items.forEach(item => {
            const entry = document.createElement('li');
            const text = document.createElement('span');
            text.textContent = item.text;
            entry.append(text);
            if (item.correct) {
                entry.classList.add('is-correct');
                const mark = document.createElement('i');
                mark.className = 'fa-light fa-circle-check';
                mark.setAttribute('aria-hidden', 'true');
                const label = document.createElement('span');
                label.className = 'sr-only';
                label.textContent = this.label('previewCorrect');
                entry.append(' ', mark, label);
            }
            if (item.detail) {
                const detail = document.createElement('p');
                detail.textContent = item.detail;
                entry.append(detail);
            }
            if (item.items.length > 0) {
                entry.append(this.renderItems(item.items));
            }
            list.append(entry);
        });

        return list;
    }

    /**
     * The strip on top of every change on the page: what kind of change it is, and its buttons.
     */
    private renderBar(set: PageChangeSetStatusItem, operation: PageChangeOperation, badgeText: string): HTMLElement {
        const bar = document.createElement('div');
        bar.className = 'howspace-ai-preview-bar';
        bar.setAttribute(PREVIEW_ATTRIBUTE, 'bar');

        const badge = document.createElement('span');
        badge.className = 'howspace-ai-preview-badge';
        badge.textContent = set.status === 'applying'
            ? this.label('previewApplying')
            : (operation.blocked === null ? badgeText : this.label('previewBlocked'));

        const actions = document.createElement('div');
        actions.className = 'howspace-ai-preview-actions';
        bar.append(badge, actions);
        // A change being applied can no longer be skipped.
        if (set.status !== 'staged') {
            return bar;
        }

        if (operation.blocked !== null) {
            const note = document.createElement('p');
            note.className = 'howspace-ai-preview-note';
            note.textContent = blockedLabelKeys(operation).map(key => this.label(key)).find(Boolean) ?? '';
            bar.append(note);
        } else if (operation.needsConfirmation) {
            bar.append(...this.renderConfirm(set, operation));
        }

        const skip = document.createElement('button');
        skip.type = 'button';
        skip.className = 'howspace-ai-preview-skip';
        skip.dataset.previewSkip = operation.id;
        skip.textContent = this.label('skip');
        skip.setAttribute(
            'aria-label',
            this.label('skipNamed').replace('%title%', operation.title || this.label('untitled')),
        );
        skip.disabled = this.options.isBlocked();
        skip.addEventListener('click', () => {
            if (!this.options.isBlocked()) {
                this.refocusAfterSkip = {skipped: operation.id, next: this.neighbourOf(operation.id)};
                this.options.skip(set, operation.id);
            }
        });
        actions.append(skip);

        return bar;
    }

    /**
     * The tick a change touching participant input needs, the same one as in the chat card.
     */
    private renderConfirm(set: PageChangeSetStatusItem, operation: PageChangeOperation): HTMLElement[] {
        const parts: HTMLElement[] = [];
        if (!operation.hadInput) {
            const note = document.createElement('p');
            note.className = 'howspace-ai-preview-note';
            note.textContent = this.label('inputSinceStaging');
            parts.push(note);
        }
        const {label, input} = renderCheckbox(
            this.label(operation.op === 'remove_item' ? 'confirmDelete' : 'confirmEdit'),
        );
        label.classList.add('howspace-ai-preview-confirm');
        input.dataset.previewConfirm = `${set.id}:${operation.id}`;
        input.checked = this.options.confirmations.isTicked(set, operation.id);
        setCheckboxDisabled(input, this.options.isBlocked());
        input.addEventListener('change', () => this.options.confirmations.set(set, operation.id, input.checked));
        parts.push(label);

        return parts;
    }

    private operationOf(key: string): PageChangeOperation | null {
        for (const set of this.sets) {
            const operation = set.operations.find(candidate => previewKey(set, candidate) === key);
            if (operation) {
                return operation;
            }
        }

        return null;
    }

    private previewElementOf(key: string): HTMLElement | null {
        const escaped = CSS.escape(key);
        return document.querySelector<HTMLElement>(
            `[data-ai-preview-placeholder="${escaped}"], [data-ai-preview-op="${escaped}"], `
            + `[${MOVED_ATTRIBUTE}="${escaped}"], [data-ai-preview-layout="${escaped}"]`,
        );
    }

    private pageElement(): HTMLElement | null {
        const pageId = this.options.currentPageId();
        return pageId === '' ? null : document.querySelector<HTMLElement>(`.page[data-page-id="${CSS.escape(pageId)}"]`);
    }

    /**
     * @param restoreMoves Whether moved widgets go back where they were. Otherwise the next sync puts them right.
     */
    private clear(restoreMoves = true): void {
        document.querySelectorAll<HTMLElement>(`.${EDITED_CLASS}, .${REMOVED_CLASS}`)
            .forEach(widget => this.undecorate(widget));
        document.querySelectorAll<HTMLElement>(`.${MOVED_CLASS}`).forEach(widget => this.undecorateMove(widget));
        document.querySelectorAll<HTMLElement>(`.${MOVED_CONTAINER_CLASS}`)
            .forEach(container => this.undecorateContainerMove(container));
        if (!restoreMoves) {
            document.querySelectorAll('[data-ai-preview-placeholder], [data-ai-preview-layout]')
                .forEach(element => element.remove());
            return;
        }
        document.querySelectorAll<HTMLElement>(`[${MOVED_ATTRIBUTE}]`).forEach(widget => this.restoreMoved(widget));
        document.querySelectorAll<HTMLElement>(`[${CONTAINER_MOVED_ATTRIBUTE}]`)
            .forEach(container => this.restoreMovedContainer(container));
        document.querySelectorAll<HTMLElement>('.howspace-ai-preview-container, .howspace-ai-preview-column')
            .forEach(element => this.removeDrawn(element));
        document.querySelectorAll(
            '[data-ai-preview-placeholder], [data-ai-preview-layout], [data-ai-preview-space], '
            + `[${ORIGIN_ATTRIBUTE}], [${CONTAINER_ORIGIN_ATTRIBUTE}]`,
        ).forEach(element => element.remove());
        document.querySelectorAll<HTMLElement>(`.${GONE_CLASS}`).forEach(element => element.classList.remove(GONE_CLASS));
        document.querySelectorAll<HTMLElement>('[data-ai-preview-width]').forEach(restoreColumnWidth);
        document.querySelectorAll<HTMLElement>('[data-ai-preview-container-width]').forEach(restoreContainerWidth);
    }

    /**
     * Watches the page while something is previewed on it, and lays the preview over it again once the page has
     * settled for a frame.
     */
    private observe(): void {
        if (this.observer !== null) {
            return;
        }
        this.observer = new MutationObserver(() => {
            this.scheduleRecheck();
            if (this.frame !== 0) {
                return;
            }
            this.frame = window.requestAnimationFrame(() => {
                this.frame = 0;
                this.sync();
            });
        });
        this.connect();
    }

    /**
     * The preview's own changes never reach the observer, so a change it sees came from the page: a live update of a
     * widget or the layout. Only a set waiting for approval can have changes that stop holding.
     */
    private scheduleRecheck(): void {
        if (!this.sets.some(set => set.status === 'staged')) {
            return;
        }
        // A page that keeps changing still gets its recheck once it has waited long enough.
        if (this.recheckSince !== 0 && Date.now() - this.recheckSince >= RECHECK_MAX_WAIT_MS) {
            return;
        }
        this.recheckSince = this.recheckSince || Date.now();
        window.clearTimeout(this.recheckTimer);
        this.recheckTimer = window.setTimeout(() => {
            this.recheckSince = 0;
            this.options.onPageChanged();
        }, RECHECK_DELAY_MS);
    }

    private connect(): void {
        this.observer?.observe(document.getElementById('main') ?? document.body, {childList: true, subtree: true});
    }

    private stopObserving(): void {
        this.observer?.disconnect();
        this.observer = null;
        if (this.frame !== 0) {
            window.cancelAnimationFrame(this.frame);
            this.frame = 0;
        }
    }

    /**
     * The preview's own changes to the page must not wake the observer, or it would redraw itself forever.
     */
    private withoutObserving(change: () => void): void {
        this.observer?.disconnect();
        try {
            change();
        } finally {
            this.connect();
        }
    }

    private label(key: string): string {
        return this.options.labels[key] || '';
    }
}

/**
 * A change shown in the preview: one waiting for approval, or one an apply has not written yet. An addition names
 * its widget before it exists, and the set says it was added a moment before the widget reaches the page, so it
 * stays previewed until that widget is on the page.
 */
const isPreviewed = (set: PageChangeSetStatusItem, operation: PageChangeOperation): boolean => {
    // New pages, changes to the menu and changes to the page itself are not drawn on the page, the card is where
    // they are reviewed.
    if (operation.widgetType === 'page' || isMenuChange(operation)) {
        return false;
    }
    if (operation.status === 'pending') {
        return true;
    }
    // A delete keeps its strip until the widget leaves the page.
    if (operation.op === 'remove_item') {
        return set.status === 'applying'
            && (operation.status === 'applying' || operation.status === 'applied')
            && operation.target !== null
            && document.querySelector(`.widget[data-widget-id="${CSS.escape(operation.target)}"]`) !== null;
    }
    const addedOrBeingWritten = operation.status === 'applying'
        || (operation.status === 'applied' && operation.op === 'add_widget');

    return set.status === 'applying'
        && addedOrBeingWritten
        && (operation.widgetId === null || document.getElementById(`widget-${operation.widgetId}`) === null);
};

const isContainerMove = (operation: PageChangeOperation): boolean => (
    operation.op === 'move_item' && operation.scope === 'container'
);

const isWidgetMove = (operation: PageChangeOperation): boolean => (
    operation.op === 'move_item' && operation.scope !== 'container'
);

/**
 * Puts a real container right after the container before it in the layout, or first, leaving a marker where it was
 * the first time it moves. One in its place already is left alone, since moving it reloads what its widgets embed.
 */
const placeContainer = (parent: HTMLElement, container: HTMLElement, previous: HTMLElement | null): void => {
    if (previousContainerOf(container) === previous) {
        return;
    }
    const containerId = container.dataset.containerId || '';
    const marked = document.querySelector(`[${CONTAINER_ORIGIN_ATTRIBUTE}="${CSS.escape(containerId)}"]`) !== null;
    if (containerId !== '' && !marked) {
        const origin = document.createElement('div');
        origin.hidden = true;
        origin.setAttribute(PREVIEW_ATTRIBUTE, 'container-origin');
        origin.setAttribute(CONTAINER_ORIGIN_ATTRIBUTE, containerId);
        container.before(origin);
    }
    if (previous !== null) {
        previous.after(container);
        return;
    }
    const first = parent.querySelector(':scope > .page-container, :scope > .howspace-ai-preview-container');
    if (first === null) {
        parent.append(container);
    } else if (first !== container) {
        first.before(container);
    }
};

/** The container shown right before this one, past the preview's markers and anything else between them. */
const previousContainerOf = (container: HTMLElement): HTMLElement | null => {
    let sibling = container.previousElementSibling;
    while (sibling !== null && !sibling.matches('.page-container, .howspace-ai-preview-container')) {
        sibling = sibling.previousElementSibling;
    }

    return sibling as HTMLElement | null;
};

/** Operation ids are unique only within their set. */
const previewKey = (set: PageChangeSetStatusItem, operation: PageChangeOperation): string => (
    `${set.id}:${operation.id}`
);

/**
 * Puts an element right after another one in a column, or at the top of the column. Returns whether it moved, since
 * moving a real widget reloads what it embeds, so one already in its place is left alone.
 */
const insertAfter = (column: HTMLElement, previous: HTMLElement | null, element: HTMLElement): boolean => {
    if (previous !== null) {
        if (previous.nextElementSibling === element) {
            return false;
        }
        markOrigin(element);
        previous.after(element);
        return true;
    }
    const first = Array.from(column.children).find(child => (
        child.classList.contains('widget')
        || child.hasAttribute('data-ai-preview-placeholder')
        || child.classList.contains('column-buttons')
    )) ?? null;
    if (first === element) {
        return false;
    }
    markOrigin(element);
    if (first === null) {
        column.append(element);
    } else {
        first.before(element);
    }
    return true;
};

/** Leaves a marker where a real widget is moved from, the first time the preview moves it. */
const markOrigin = (element: HTMLElement): void => {
    const widgetId = element.dataset.widgetId;
    if (!widgetId || !element.isConnected || document.querySelector(`[${ORIGIN_ATTRIBUTE}="${CSS.escape(widgetId)}"]`)) {
        return;
    }
    const origin = document.createElement('span');
    origin.hidden = true;
    origin.setAttribute(PREVIEW_ATTRIBUTE, 'origin');
    origin.setAttribute(ORIGIN_ATTRIBUTE, widgetId);
    element.before(origin);
};

/**
 * Shows a column at the width a change gives it. The width the page gives it is in `data-width`, which live updates
 * keep current, so once that changes from the width the preview started from, the page's width shows.
 */
const previewColumnWidth = (column: HTMLElement, width: number): void => {
    const own = column.dataset.width ?? '';
    if (own === String(width)) {
        restoreColumnWidth(column);
        return;
    }
    if (column.dataset.aiPreviewWidth === undefined) {
        column.dataset.aiPreviewWidthFrom = own;
    } else if (column.dataset.aiPreviewWidthFrom !== own) {
        return;
    }
    column.dataset.aiPreviewWidth = String(width);
    column.style.width = `${width}%`;
};

/** Gives a column the width the page gives it. */
const restoreColumnWidth = (column: HTMLElement): void => {
    if (column.dataset.width) {
        column.style.width = `${column.dataset.width}%`;
    }
    delete column.dataset.aiPreviewWidth;
    delete column.dataset.aiPreviewWidthFrom;
};

/** Shows a container at the width a change gives it, the same way, from `data-container-width`. */
const previewContainerWidth = (container: HTMLElement, width: string): void => {
    const own = container.dataset.containerWidth ?? '';
    const content = container.querySelector<HTMLElement>(':scope > .page-container-content');
    if (content === null) {
        return;
    }
    if (own === width) {
        restoreContainerWidth(container);
        return;
    }
    if (container.dataset.aiPreviewContainerWidth === undefined) {
        container.dataset.aiPreviewContainerWidthFrom = own;
    } else if (container.dataset.aiPreviewContainerWidthFrom !== own) {
        return;
    }
    content.classList.remove(...WIDTH_CLASSES);
    content.classList.add(`${width}-width`);
    container.dataset.aiPreviewContainerWidth = width;
};

/** Gives a container the width the page gives it. */
const restoreContainerWidth = (container: HTMLElement): void => {
    const content = container.querySelector<HTMLElement>(':scope > .page-container-content');
    if (content !== null && container.dataset.containerWidth) {
        content.classList.remove(...WIDTH_CLASSES);
        content.classList.add(`${container.dataset.containerWidth}-width`);
    }
    delete container.dataset.aiPreviewContainerWidth;
    delete container.dataset.aiPreviewContainerWidthFrom;
};

/** The widgets on the page by id. A live update can leave two copies of one for a moment. */
const widgetsOf = (page: HTMLElement): Map<string, HTMLElement[]> => {
    const widgets = new Map<string, HTMLElement[]>();
    page.querySelectorAll<HTMLElement>('.widget[data-widget-id]').forEach(widget => {
        const id = widget.dataset.widgetId || '';
        widgets.set(id, [...(widgets.get(id) ?? []), widget]);
    });

    return widgets;
};

/** Removes a stale copy of a widget the way the page removes a widget, so what it runs stops with it. */
const discardWidget = (widget: HTMLElement): void => {
    try {
        howspace.widgets?.destroyWidget?.(jQuery(widget));
    } catch {
        // Nothing of it is shown anymore either way.
    }
    widget.remove();
};

/** Keeps focus on the page when the preview control that had it goes away. */
const focusWithoutScrolling = (element: HTMLElement): void => {
    if (!element.hasAttribute('tabindex')) {
        element.tabIndex = -1;
        element.addEventListener('blur', () => element.removeAttribute('tabindex'), {once: true});
    }
    element.focus({preventScroll: true});
};
